"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "~/lib/utils";

export const NEVER_MISS_HEADLINES = [
  "Never miss a call",
  "Never miss a visitor",
  "Never miss an email",
  "Never miss a text message",
  "Never miss a video call",
  "Never miss a booking",
  "Never miss an opportunity",
  "Always a human in the loop.",
] as const;

const LONGEST_HEADLINE = NEVER_MISS_HEADLINES.reduce(
  (longest, current) => (current.length > longest.length ? current : longest),
  NEVER_MISS_HEADLINES[0]!,
);

const SCREEN_READER_HEADLINE =
  "Never miss a call, visitor, email, text message, video call, booking, or opportunity. Always a human in the loop.";

const DEFAULT_HEADLINE_CLASS =
  "mb-4 w-full text-center text-[clamp(1.125rem,3.1vw+0.6rem,3.75rem)] font-bold leading-[1.2] tracking-tight sm:leading-tight md:text-5xl lg:text-[3.25rem] lg:leading-[1.1] xl:text-6xl xl:leading-[1.08]";

type NeverMissHeroHeadlineProps = {
  className?: string;
};

export function NeverMissHeroHeadline({ className }: NeverMissHeroHeadlineProps) {
  const [headlineIndex, setHeadlineIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setHeadlineIndex((prev) => (prev + 1) % NEVER_MISS_HEADLINES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const headline = NEVER_MISS_HEADLINES[headlineIndex]!;

  return (
    <h1 className={cn(DEFAULT_HEADLINE_CLASS, className)}>
      <span className="sr-only">{SCREEN_READER_HEADLINE}</span>
      <span aria-hidden className="relative mx-auto inline-block w-full max-w-full text-center">
        <span
          className="invisible mx-auto block max-w-full px-1 text-balance sm:whitespace-nowrap"
          aria-hidden
        >
          {LONGEST_HEADLINE}
        </span>
        <AnimatePresence mode="wait">
          <motion.span
            key={headline}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.35 }}
            className="absolute inset-0 flex items-center justify-center px-1 text-balance sm:whitespace-nowrap"
          >
            {headline}
          </motion.span>
        </AnimatePresence>
      </span>
    </h1>
  );
}
